// Regras puras dos avisos "o cliente respondeu" (sem dependências, testáveis
// com node:test). O servidor guarda uma linha por atendimento do atendente.

export const REPLY_NOTIFICATION_TTL_MS = 24 * 60 * 60 * 1000;
export const REPLY_HISTORY_LIMIT = 100;

const timeOf = value => {
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : 0;
};

const sameId = (a, b) => String(a) === String(b);

// /tickets/123 é a tela da conversa. /tickets sozinho é só a lista.
export const ticketIdFromPath = pathname => {
  const match = /^\/tickets\/([1-9]\d*)\/?$/.exec(String(pathname || ""));
  return match ? Number(match[1]) : null;
};

export const isReplyActive = notification => !!notification && !notification.seenAt;

export const isWithinReplyHistory = (notification, now = Date.now()) =>
  !!notification && timeOf(notification.notifiedAt) >= now - REPLY_NOTIFICATION_TTL_MS;

// Entre duas versões da mesma linha vale a atualizada por último no servidor.
const newest = (current, incoming) => {
  if (!current) return incoming;
  if (!incoming) return current;
  return timeOf(incoming.updatedAt) >= timeOf(current.updatedAt) ? incoming : current;
};

export const normalizeReplyNotifications = (list, now = Date.now()) => {
  const byTicket = new Map();
  (list || []).forEach(item => {
    if (!item || item.id === undefined || item.id === null) return;
    if (!isWithinReplyHistory(item, now)) return;
    const key = String(item.ticketId);
    byTicket.set(key, newest(byTicket.get(key), item));
  });
  return [...byTicket.values()]
    .sort((a, b) => timeOf(b.notifiedAt) - timeOf(a.notifiedAt) || Number(b.id) - Number(a.id))
    .slice(0, REPLY_HISTORY_LIMIT);
};

// Eventos em tempo real chegam na ordem em que o servidor os emitiu: o mais
// recente substitui a linha daquele atendimento, sem comparar relógios.
export const upsertReplyNotification = (list, incoming, now = Date.now()) => {
  if (!incoming) return list;
  const rest = (list || []).filter(item => !sameId(item.id, incoming.id) && !sameId(item.ticketId, incoming.ticketId));
  return normalizeReplyNotifications([incoming, ...rest], now);
};

// Resultado da API. Atendimentos com "visto" ainda a caminho do servidor
// continuam vistos, para o contador não piscar.
export const applyFetchedReplyNotifications = (fetched, pendingSeenTicketIds = [], now = Date.now()) => {
  const pending = new Set([...pendingSeenTicketIds].map(String));
  const seenAt = new Date(now).toISOString();
  return normalizeReplyNotifications(
    (fetched || []).map(item => (pending.has(String(item.ticketId)) && !item.seenAt ? { ...item, seenAt } : item)),
    now
  );
};

export const applyReplySeen = (list, event) => {
  if (!event) return list;
  const seenAt = event.seenAt || new Date().toISOString();
  const ids = new Set((event.ids || []).map(String));
  let changed = false;
  const next = (list || []).map(item => {
    const matches = ids.has(String(item.id)) || (event.ticketOnly && sameId(item.ticketId, event.ticketId));
    if (!matches || item.seenAt) return item;
    changed = true;
    const updatedAt = timeOf(item.updatedAt) > timeOf(seenAt) ? item.updatedAt : seenAt;
    return { ...item, seenAt, updatedAt };
  });
  return changed ? next : list;
};

export const hasActiveReplyForTicket = (list, ticketId) =>
  (list || []).some(item => isReplyActive(item) && sameId(item.ticketId, ticketId));

export const activeReplyCount = list => (list || []).filter(isReplyActive).length;

// Como avisar ao chegar uma resposta:
// - "viewing": a conversa já está na tela (aba visível) → conta como vista;
// - "subtle": estou atendendo outro cliente na tela → aviso discreto;
// - "normal": fora da conversa ou com a aba escondida → aviso completo.
export const replyPresentation = ({ ticketId, viewingTicketId, visible }) => {
  if (visible && viewingTicketId && sameId(viewingTicketId, ticketId)) return "viewing";
  if (visible && viewingTicketId) return "subtle";
  return "normal";
};

const pad = value => String(value).padStart(2, "0");
const clock = date => pad(date.getHours()) + ":" + pad(date.getMinutes());
const dayKey = date => date.getFullYear() + "-" + date.getMonth() + "-" + date.getDate();

export const replyTimeLabel = (value, now = Date.now()) => {
  const time = timeOf(value);
  if (!time) return "";
  const diff = Math.max(0, now - time);
  if (diff < 60 * 1000) return "agora";
  if (diff < 60 * 60 * 1000) return "há " + Math.floor(diff / 60000) + " min";
  const date = new Date(time);
  const today = new Date(now);
  if (dayKey(date) === dayKey(today)) return clock(date);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dayKey(date) === dayKey(yesterday)) return "ontem, " + clock(date);
  return pad(date.getDate()) + "/" + pad(date.getMonth() + 1) + " " + clock(date);
};

export const replyCountLabel = notification => {
  const count = Number(notification?.unseenCount) || 0;
  if (!isReplyActive(notification) || count <= 1) return "";
  return count > 99 ? "99+ novas" : count + " novas";
};
